import React, { useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calculator, Zap, Target, TrendingUp, Gift, ArrowRight,
  Loader2, ChevronRight, Dumbbell, Sun, Trophy, Banknote,
  ShoppingBag, Shield, Ticket, Sparkles, RotateCcw
} from 'lucide-react';

const fadeUp = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 } };

const GOALS = [
  { id: 'maximize_value', label: 'Max Value', desc: 'Get the most bang for your $PLAY', icon: TrendingUp, color: 'blue' },
  { id: 'save_for_gear', label: 'Save for Gear', desc: 'Build toward equipment or merch', icon: ShoppingBag, color: 'orange' },
  { id: 'cash_out', label: 'Cash Out', desc: 'Convert to GCash/Maya', icon: Banknote, color: 'lime' },
  { id: 'balanced', label: 'Balanced', desc: 'Mix of discounts, gear & cash', icon: Target, color: 'purple' },
];

const SPORTS = ['Basketball', 'Badminton', 'Football', 'Tennis', 'Volleyball', 'Table Tennis'];

export default function PlannerPage() {
  const { api } = useAuth();
  const [sessions, setSessions] = useState(8);
  const [offPeak, setOffPeak] = useState(2);
  const [sport, setSport] = useState('');
  const [goal, setGoal] = useState('maximize_value');
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);

  const generatePlan = useCallback(async () => {
    setLoading(true);
    setPlan(null);
    try {
      const res = await api.post('/planner/generate', {
        sessions_planned: sessions,
        off_peak_sessions: offPeak,
        preferred_sport: sport || null,
        goal,
      });
      setPlan(res.data.plan);
    } catch (err) {
      console.error('Planner error:', err);
      alert(err.response?.data?.detail || 'Failed to generate plan');
    } finally {
      setLoading(false);
    }
  }, [api, sessions, offPeak, sport, goal]);

  const reset = () => {
    setPlan(null);
    setSessions(8);
    setOffPeak(2);
    setGoal('maximize_value');
  };

  return (
    <div data-testid="planner-page" className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <motion.div {...fadeUp}>
        <h1 className="font-barlow text-2xl md:text-3xl font-bold text-white tracking-tight flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-lime-500 flex items-center justify-center shadow-[0_0_25px_rgba(59,130,246,0.3)]">
            <Calculator size={22} className="text-white" />
          </div>
          Rewards Planner
        </h1>
        <p className="text-slate-400 text-sm font-manrope mt-1">
          Plan your month. See what you'll earn. Get a smart redemption strategy.
        </p>
      </motion.div>

      {/* Input Form */}
      {!plan ? (
        <div className="space-y-6">
          {/* Sessions Slider */}
          <motion.div {...fadeUp} transition={{ delay: 0.1 }}
            className="bg-slate-950/60 backdrop-blur-xl border border-white/10 rounded-xl p-6"
          >
            <div className="flex items-center gap-2 mb-5">
              <Dumbbell size={18} className="text-blue-400" />
              <h3 className="font-barlow text-sm font-bold text-white uppercase tracking-wider">How many sessions this month?</h3>
            </div>

            <div className="flex items-center gap-6 mb-4">
              <input
                data-testid="sessions-slider"
                type="range"
                min="1" max="30" value={sessions}
                onChange={e => setSessions(parseInt(e.target.value))}
                className="flex-1 h-2 rounded-full appearance-none bg-slate-700 accent-blue-500 cursor-pointer"
              />
              <div className="w-20 text-center">
                <span className="font-barlow text-4xl font-bold text-white">{sessions}</span>
                <p className="text-[10px] text-slate-500 uppercase tracking-wider">sessions</p>
              </div>
            </div>

            {/* Quick presets */}
            <div className="flex gap-2">
              {[4, 8, 12, 16, 20].map(n => (
                <button key={n}
                  data-testid={`preset-${n}`}
                  onClick={() => setSessions(n)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                    sessions === n ? 'bg-blue-600 text-white' : 'bg-white/5 text-slate-400 hover:text-white hover:bg-white/10'
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
          </motion.div>

          {/* Off-Peak & Sport */}
          <div className="grid md:grid-cols-2 gap-4">
            <motion.div {...fadeUp} transition={{ delay: 0.15 }}
              className="bg-slate-950/60 backdrop-blur-xl border border-white/10 rounded-xl p-5"
            >
              <div className="flex items-center gap-2 mb-4">
                <Sun size={16} className="text-lime-400" />
                <h3 className="font-barlow text-xs font-bold text-white uppercase tracking-wider">Off-Peak Sessions</h3>
              </div>
              <div className="flex items-center gap-4">
                <input
                  data-testid="offpeak-slider"
                  type="range"
                  min="0" max={sessions} value={Math.min(offPeak, sessions)}
                  onChange={e => setOffPeak(parseInt(e.target.value))}
                  className="flex-1 h-1.5 rounded-full appearance-none bg-slate-700 accent-lime-400 cursor-pointer"
                />
                <div className="text-center w-12">
                  <span className="font-barlow text-2xl font-bold text-lime-400">{Math.min(offPeak, sessions)}</span>
                </div>
              </div>
              <p className="text-slate-500 text-[10px] mt-2">Off-peak = mornings/late evenings (+10 $PLAY bonus each)</p>
            </motion.div>

            <motion.div {...fadeUp} transition={{ delay: 0.2 }}
              className="bg-slate-950/60 backdrop-blur-xl border border-white/10 rounded-xl p-5"
            >
              <div className="flex items-center gap-2 mb-4">
                <Target size={16} className="text-blue-400" />
                <h3 className="font-barlow text-xs font-bold text-white uppercase tracking-wider">Preferred Sport</h3>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {SPORTS.map(s => (
                  <button key={s}
                    onClick={() => setSport(sport === s ? '' : s)}
                    className={`px-3 py-1.5 rounded-full text-xs transition-all ${
                      sport === s ? 'bg-blue-600 text-white' : 'bg-white/5 text-slate-400 hover:text-white'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Goal Selection */}
          <motion.div {...fadeUp} transition={{ delay: 0.25 }}>
            <div className="flex items-center gap-2 mb-3">
              <Trophy size={16} className="text-lime-400" />
              <h3 className="font-barlow text-xs font-bold text-white uppercase tracking-wider">What's your goal?</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {GOALS.map(g => {
                const active = goal === g.id;
                return (
                  <button key={g.id}
                    data-testid={`goal-${g.id}`}
                    onClick={() => setGoal(g.id)}
                    className={`p-4 rounded-xl border text-left transition-all ${
                      active
                        ? 'bg-blue-600/15 border-blue-500/30 scale-[1.02]'
                        : 'bg-slate-950/60 border-white/5 hover:border-white/15'
                    }`}
                  >
                    <g.icon size={20} className={active ? 'text-blue-400' : 'text-slate-500'} />
                    <p className={`font-barlow text-sm font-bold mt-2 ${active ? 'text-white' : 'text-slate-300'}`}>{g.label}</p>
                    <p className="text-slate-500 text-[10px] mt-0.5">{g.desc}</p>
                  </button>
                );
              })}
            </div>
          </motion.div>

          {/* Generate Button */}
          <motion.div {...fadeUp} transition={{ delay: 0.3 }}>
            <button
              data-testid="generate-plan-btn"
              onClick={generatePlan}
              disabled={loading || sessions < 1}
              className="w-full py-4 rounded-2xl bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white font-barlow text-lg font-bold uppercase tracking-wider shadow-[0_0_30px_rgba(59,130,246,0.3)] hover:shadow-[0_0_40px_rgba(59,130,246,0.5)] transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-3"
            >
              {loading ? (
                <><Loader2 size={22} className="animate-spin" /> AI is planning your month...</>
              ) : (
                <><Sparkles size={22} /> Generate My Rewards Plan</>
              )}
            </button>
          </motion.div>
        </div>
      ) : (
        /* ═══ PLAN RESULTS ═══ */
        <div className="space-y-5">
          {/* Reset */}
          <motion.div {...fadeUp} className="flex justify-end">
            <button data-testid="reset-plan-btn" onClick={reset}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-white/10 text-slate-300 hover:bg-white/5 text-xs transition-all"
            >
              <RotateCcw size={14} /> New Plan
            </button>
          </motion.div>

          {/* Projected Earnings */}
          <motion.div {...fadeUp} transition={{ delay: 0.05 }}
            className="bg-gradient-to-br from-blue-600/20 to-lime-500/10 border border-blue-500/20 rounded-2xl p-6 relative overflow-hidden"
            data-testid="projected-earnings"
          >
            <div className="absolute top-0 right-0 w-40 h-40 bg-lime-400/5 rounded-full blur-[80px]" />
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp size={18} className="text-lime-400" />
              <h3 className="font-barlow text-sm font-bold text-white uppercase tracking-wider">Projected Earnings</h3>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              {[
                { label: 'Base', value: plan.projected_earnings?.base_earnings || 0, color: 'text-white' },
                { label: 'Streak', value: plan.projected_earnings?.streak_bonus || 0, color: 'text-blue-400' },
                { label: 'Off-Peak', value: plan.projected_earnings?.off_peak_bonus || 0, color: 'text-lime-400' },
                { label: 'Attendance', value: plan.projected_earnings?.attendance_bonus || 0, color: 'text-purple-400' },
                { label: 'TOTAL', value: plan.projected_earnings?.total_projected || 0, color: 'text-lime-400', big: true },
              ].map((s, i) => (
                <div key={i} className={`${s.big ? 'bg-lime-500/10 border border-lime-400/20' : 'bg-white/5'} rounded-xl p-3 text-center`}>
                  <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">{s.label}</p>
                  <p className={`font-barlow ${s.big ? 'text-2xl' : 'text-xl'} font-bold ${s.color}`}>
                    +{s.value}
                  </p>
                </div>
              ))}
            </div>

            <p className="text-slate-400 text-xs font-mono">{plan.projected_earnings?.breakdown}</p>

            <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between">
              <span className="text-slate-400 text-sm">Total Available (current + projected)</span>
              <span className="font-barlow text-2xl font-bold text-white">{plan.total_available || 0} <span className="text-blue-400 text-sm">$PLAY</span></span>
            </div>
          </motion.div>

          {/* Recommended Strategy */}
          {plan.recommended_strategy && (
            <motion.div {...fadeUp} transition={{ delay: 0.15 }}
              className="bg-slate-950/60 backdrop-blur-xl border border-lime-400/20 rounded-2xl p-6"
              data-testid="recommended-strategy"
            >
              <div className="flex items-center gap-2 mb-1">
                <Sparkles size={18} className="text-lime-400" />
                <h3 className="font-barlow text-lg font-bold text-white">{plan.recommended_strategy.title}</h3>
              </div>
              <p className="text-slate-400 text-sm mb-5">{plan.recommended_strategy.summary}</p>

              {/* Steps */}
              <div className="space-y-3">
                {(plan.recommended_strategy.steps || []).map((step, i) => (
                  <motion.div key={i} {...fadeUp} transition={{ delay: 0.2 + i * 0.06 }}
                    className="flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/5 hover:border-lime-400/15 transition-all group"
                    data-testid={`strategy-step-${i}`}
                  >
                    <div className="w-8 h-8 rounded-full bg-lime-500/20 text-lime-400 flex items-center justify-center font-barlow font-bold text-sm flex-shrink-0">
                      {step.order || i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium">{step.action}</p>
                      <p className="text-slate-500 text-xs">{step.value_description}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-blue-400 font-barlow font-bold text-sm">{step.play_cost} $PLAY</p>
                      {step.value_php > 0 && <p className="text-lime-400 text-[10px]">≈ ₱{step.value_php}</p>}
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Totals */}
              <div className="mt-5 pt-4 border-t border-white/10 grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider">Total Spent</p>
                  <p className="font-barlow text-xl font-bold text-blue-400">{plan.recommended_strategy.total_play_spent} $PLAY</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider">Total Value</p>
                  <p className="font-barlow text-xl font-bold text-lime-400">₱{plan.recommended_strategy.total_value_php}</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider">Remaining</p>
                  <p className="font-barlow text-xl font-bold text-white">{plan.recommended_strategy.remaining_balance} $PLAY</p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Alternative Strategy */}
          {plan.alternative_strategy && (
            <motion.div {...fadeUp} transition={{ delay: 0.3 }}
              className="bg-slate-950/60 border border-white/10 rounded-xl p-4 flex items-center gap-4"
              data-testid="alt-strategy"
            >
              <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center text-slate-400 flex-shrink-0">
                <ArrowRight size={18} />
              </div>
              <div className="flex-1">
                <p className="text-white text-sm font-medium">Alternative: {plan.alternative_strategy.title}</p>
                <p className="text-slate-400 text-xs">{plan.alternative_strategy.summary}</p>
              </div>
              <span className="text-slate-300 font-barlow font-bold text-sm">≈ ₱{plan.alternative_strategy.total_value_php}</span>
            </motion.div>
          )}

          {/* Motivation & Pro Tip */}
          <div className="grid md:grid-cols-2 gap-4">
            {plan.motivation && (
              <motion.div {...fadeUp} transition={{ delay: 0.35 }}
                className="bg-gradient-to-r from-blue-600/10 to-transparent border border-blue-500/10 rounded-xl p-4"
              >
                <div className="flex items-center gap-1.5 mb-2">
                  <Zap size={14} className="text-blue-400" />
                  <span className="text-[10px] text-blue-400 uppercase tracking-wider font-bold">Motivation</span>
                </div>
                <p className="text-slate-200 text-sm italic">{plan.motivation}</p>
              </motion.div>
            )}
            {plan.pro_tip && (
              <motion.div {...fadeUp} transition={{ delay: 0.4 }}
                className="bg-gradient-to-r from-lime-500/10 to-transparent border border-lime-400/10 rounded-xl p-4"
                data-testid="pro-tip"
              >
                <div className="flex items-center gap-1.5 mb-2">
                  <Sparkles size={14} className="text-lime-400" />
                  <span className="text-[10px] text-lime-400 uppercase tracking-wider font-bold">Pro Tip</span>
                </div>
                <p className="text-slate-200 text-sm">{plan.pro_tip}</p>
              </motion.div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
